import type {
  BuilderMessage,
  PendingComposioExec,
  PendingRuntimeInput,
  PendingUserQuestion,
} from "./types";
import { isUserQuestionAnsweredInTranscript } from "./user-question-answered";
import { settledComposioApprovalIds } from "./hitl-settled";

export type SessionHitlSnapshot = {
  pendingUserQuestion?: Omit<PendingUserQuestion, "sessionId"> | null;
  pendingComposioExecApprovals?: Array<Omit<PendingComposioExec, "sessionId" | "status">>;
  pendingRuntimeInputRequests?: Array<
    PendingRuntimeInput & { requestId: string; createdAt?: string }
  >;
  pauseReason?: { fields?: PendingRuntimeInput["fields"] } | null;
  status?: string;
  /** Soft-parked durable run — do not wipe live HITL cards when still set. */
  durableRunId?: string | null;
  /** True while a soft HITL card is waiting (status may still say planning). */
  parkedForInput?: boolean;
  /** Already-submitted values — skip resurrecting parks the user answered. */
  collectedInputs?: Record<string, unknown> | null;
};

type HitlTarget = {
  messages?: BuilderMessage[];
  pendingComposioExecQueue?: PendingComposioExec[];
  pendingUserQuestion?: PendingUserQuestion | null;
  resolvedRuntimeRequestId?: string | null;
  setPendingUserQuestion: (value: PendingUserQuestion | null) => void;
  enqueuePendingComposioExec: (value: PendingComposioExec) => void;
  removePendingComposioExec?: (id: string) => void;
  setPendingRuntimeInput: (value: PendingRuntimeInput | null) => void;
  setPhase: (value: string) => void;
};

export type ApplySessionHitlResult = {
  restored: boolean;
  /** Local approval cards removed because FlowOps no longer has them. */
  clearedStaleComposio: boolean;
};

function runtimeFieldsAlreadyCollected(
  fields: PendingRuntimeInput["fields"] | undefined,
  collected: Record<string, unknown> | null | undefined,
): boolean {
  if (!fields?.length || !collected) return false;
  return fields.every((field) => {
    if (field.required === false) return true;
    const value = collected[field.name];
    if (value === undefined || value === null) return false;
    if (typeof value === "string" && !value.trim()) return false;
    return true;
  });
}

/**
 * Apply server HITL and drop local Composio cards the server no longer has.
 *
 * Soft-park races: while `durableRunId` is set and the server pending map is
 * still empty (persist lag), keep local cards instead of wiping them.
 */
export function reconcileSessionHitl(
  target: HitlTarget,
  sessionId: string,
  snapshot: SessionHitlSnapshot,
): ApplySessionHitlResult {
  const planParked = snapshot.status === "awaiting_approval";
  const localQueue = target.pendingComposioExecQueue ?? [];

  if (planParked) {
    let clearedStaleComposio = false;
    if (target.removePendingComposioExec && localQueue.length) {
      for (const local of [...localQueue]) {
        target.removePendingComposioExec(local.approvalId);
        clearedStaleComposio = true;
      }
    }
    if (target.pendingUserQuestion) {
      target.setPendingUserQuestion(null);
    }
    target.setPhase("awaiting approval");
    return { restored: false, clearedStaleComposio };
  }

  const serverApprovals = snapshot.pendingComposioExecApprovals ?? [];
  const serverApprovalIds = new Set(
    serverApprovals
      .map((pending) => pending?.approvalId?.trim())
      .filter((id): id is string => Boolean(id)),
  );
  const parkedSoft =
    Boolean(snapshot.durableRunId?.trim()) &&
    snapshot.status !== "complete" &&
    snapshot.status !== "error";

  let clearedStaleComposio = false;
  if (target.removePendingComposioExec && localQueue.length) {
    if (serverApprovalIds.size > 0 || !parkedSoft) {
      for (const local of [...localQueue]) {
        if (!serverApprovalIds.has(local.approvalId)) {
          target.removePendingComposioExec(local.approvalId);
          clearedStaleComposio = true;
        }
      }
    }
  }

  let restored = false;
  const decidedComposio = settledComposioApprovalIds(target.messages ?? []);

  for (const pending of serverApprovals) {
    if (!pending?.approvalId || !pending.toolSlug) continue;
    // Transcript already has Approve/Reject for this id — never re-arm Accept.
    if (decidedComposio.has(pending.approvalId.trim())) continue;
    target.enqueuePendingComposioExec({
      ...pending,
      sessionId,
      arguments: pending.arguments ?? {},
      reason: pending.reason || "Approval required",
      status: "awaiting",
    });
    restored = true;
  }

  if (
    snapshot.pendingUserQuestion?.questionId &&
    Array.isArray(snapshot.pendingUserQuestion.questions) &&
    snapshot.pendingUserQuestion.questions.length
  ) {
    const questionId = snapshot.pendingUserQuestion.questionId;
    // Lagging park after Submit: transcript already has the answer — leave UI idle.
    if (!isUserQuestionAnsweredInTranscript(target.messages ?? [], questionId)) {
      target.setPendingUserQuestion({
        ...snapshot.pendingUserQuestion,
        sessionId,
      });
      restored = true;
    }
  } else if (
    snapshot.pendingUserQuestion === null ||
    (snapshot.pendingUserQuestion === undefined && !parkedSoft)
  ) {
    if (target.pendingUserQuestion && snapshot.pendingUserQuestion === null) {
      target.setPendingUserQuestion(null);
    }
  }

  const runtimeRequests = snapshot.pendingRuntimeInputRequests ?? [];
  // Prefer the newest park — Object.values order keeps zombies first after stacks.
  const runtime = [...runtimeRequests].sort((left, right) => {
    const leftAt = Date.parse(String(left.createdAt ?? "")) || 0;
    const rightAt = Date.parse(String(right.createdAt ?? "")) || 0;
    return rightAt - leftAt;
  })[0];
  const resolvedRuntimeId = target.resolvedRuntimeRequestId?.trim() || "";
  if (runtime?.fields?.length) {
    const runtimeId = runtime.requestId?.trim() || "";
    // Local submit already resolved this requestId — don't resurrect it.
    if (runtimeId && runtimeId === resolvedRuntimeId) {
      target.setPendingRuntimeInput(null);
    } else {
      // Server still has an open park — always show it. Do not skip just because
      // collectedInputs already has values (re-ask after invalid fixture / new branch).
      // FlowOps pendingHitlFromSnapshot does the same.
      target.setPendingRuntimeInput({ ...runtime, blocking: true });
      restored = true;
    }
  } else if (snapshot.pauseReason?.fields?.length) {
    if (runtimeFieldsAlreadyCollected(snapshot.pauseReason.fields, snapshot.collectedInputs)) {
      target.setPendingRuntimeInput(null);
    } else {
      target.setPendingRuntimeInput({ fields: snapshot.pauseReason.fields });
      restored = true;
    }
  } else if (Array.isArray(snapshot.pendingRuntimeInputRequests)) {
    if (snapshot.pendingRuntimeInputRequests.length === 0 && !parkedSoft) {
      target.setPendingRuntimeInput(null);
    } else if (snapshot.pendingRuntimeInputRequests.length === 0 && parkedSoft) {
      // Keep local runtime card if soft-park persist lagged.
    } else {
      target.setPendingRuntimeInput(null);
    }
  }

  if (restored) {
    target.setPhase(
      snapshot.status === "paused" || snapshot.status === "awaiting_input"
        ? snapshot.status
        : "awaiting input",
    );
  } else if (snapshot.status === "awaiting_input" || snapshot.status === "paused") {
    target.setPhase(snapshot.status);
  }

  return { restored, clearedStaleComposio };
}
